import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";

/**
 * ScrollLineBackground
 * - Full-page SVG line that "draws" as user scrolls
 * - Put this at zIndex: 1 and your content at zIndex: 2
 * - ✅ Desktop keeps current line; Mobile uses a redesigned path
 */
export default function ScrollLineBackground({
  prefersReducedMotion = false,
  opacity = 0.35,
  strokeWidth = 2,
  start = 0.0,
  end = 1.0,
}) {
  const pathRef = useRef(null);
  const [dash, setDash] = useState({ length: 1, offset: 1 });

  const isMobile = useMediaQuery("(max-width:600px)");

  const viewBoxDesktop = useMemo(() => "0 0 1000 5200", []);
  const viewBoxMobile = useMemo(() => "0 0 360 5200", []);

  // ✅ Desktop path (UNCHANGED)
  const dDesktop = useMemo(
    () => `
      M 90 220
      C 170 120, 310 120, 390 220
      C 470 320, 520 420, 610 520
      C 700 620, 840 650, 915 800 

      C 980 930, 930 1030, 820 1100
      C 700 1175, 540 1220, 460 1340
      C 380 1460, 430 1600, 580 1680
      C 740 1765, 900 1860, 890 2020

      C 880 2180, 730 2260, 580 2325
      C 420 2395, 310 2485, 300 2630
      C 290 2770, 390 2905, 560 2965
      C 740 3030, 920 3120, 900 3300

      C 880 3480, 690 3560, 520 3630
      C 350 3700, 250 3820, 270 3960
      C 295 4125, 470 4240, 650 4300
      C 840 4365, 960 4510, 910 4680

      C 860 4860, 660 4920, 470 5000
      C 320 5060, 210 5140, 140 5180
    `,
    []
  );

  /**
   * ✅ Mobile redesigned path
   * - Centered around x=180
   * - Smooth “S” waves (no sharp turns / no self-crossing)
   * - Stays inside 0..360 with safe margins (≈ 45..315)
   */
  const dMobile = useMemo(
    () => `
      M 180 180

      C 300 260, 300 520, 180 640
      C  60 760,  60 980, 180 1120
      C 300 1260, 300 1500, 180 1640
      C  60 1780,  60 2020, 180 2160

      C 315 2320, 315 2580, 180 2760
      C  0 2940,  0 3180, 180 3360
      C 360 3540, 360 3900, 180 4000
      C  -50 4100,  -50 4620, 180 4800

      C 360 4900, 360 5300, 180 5400
      C  -50 5510,  -50 5900, 75 6000
    `,
    []
  );

  const activeD = isMobile ? dMobile : dDesktop;
  const activeViewBox = isMobile ? viewBoxMobile : viewBoxDesktop;

  // get path length whenever path changes (so dash fits)
  useEffect(() => {
    const p = pathRef.current;
    if (!p) return;
    try {
      const len = p.getTotalLength();
      setDash({ length: len, offset: len });
    } catch (e) {
      // ignore
    }
  }, [activeD]);

  useEffect(() => {
    if (prefersReducedMotion) {
      setDash((d) => ({ ...d, offset: 0 }));
      return;
    }

    let raf = 0;

    const calcProgress = () => {
      const doc = document.documentElement;
      const scrollTop = window.pageYOffset || doc.scrollTop || 0;
      const maxScroll = Math.max(1, doc.scrollHeight - window.innerHeight);

      let p = scrollTop / maxScroll; // 0..1
      const span = Math.max(0.0001, end - start);
      p = (p - start) / span;
      p = Math.max(0, Math.min(1, p));
      return p;
    };

    const tick = () => {
      raf = 0;
      const p = calcProgress();
      setDash((d) => {
        const len = d.length || 1;
        return { ...d, offset: len * (1 - p) };
      });
    };

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(tick);
    };

    tick();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [prefersReducedMotion, start, end]);

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <Box
        component="svg"
        viewBox={activeViewBox}
        preserveAspectRatio="none"
        sx={{
          width: "100%",
          height: "100%",
          display: "block",
          opacity,
        }}
      >
        <path
          ref={pathRef}
          d={activeD}
          fill="none"
          stroke={getPrimaryColor("b2b")}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: dash.length,
            strokeDashoffset: dash.offset,
            transition: prefersReducedMotion
              ? "none"
              : "stroke-dashoffset 0.06s linear",
          }}
        />
      </Box>
    </Box>
  );
}
